// The admin Courses page's table configuration and row actions. Page-backed: the server searches, filters,
// sorts and pages (account/lib/admin_courses.py), so a column's value and searchText describe the cell for
// the component's checks rather than drive a client-side query.

import React from 'react'

import { masterOf } from './cells/MasterCell'
import {
  AiCell,
  CourseNumberCell,
  InstructorBadges,
  MasterCell,
  StudentsCell,
  TypeCell,
} from './cells'

export const TABLE_ID = 'admin-courses'
export const STATUS_OPTIONS = [
  'Published',
  'Unpublished',
  'Archived',
  'Deleted',
]
export const TYPE_OPTIONS = ['Master', 'Demo', 'Regular']
export const PAGE_SIZES = [25, 50, 100, 200]

const size = (width, minWidth, maxWidth) => ({ width, minWidth, maxWidth })

export const columns = () => [
  {
    key: 'title',
    label: 'Course Title',
    type: 'link',
    required: true,
    lock: 'start',
    wrap: 'ellipsis',
    sortable: true,
    searchable: true,
    searchText: row => row.title.label,
    ...size(220, 160, 280),
  },
  {
    key: 'coursenum',
    label: 'Course Number',
    type: 'text',
    required: true,
    wrap: 'ellipsis',
    sortable: true,
    searchable: true,
    value: row => row.coursenum,
    searchText: row =>
      [row.coursenum, row.master_coursenum].filter(Boolean).join(' '),
    render: row => <CourseNumberCell row={row} />,
    ...size(130, 110, 480),
  },
  {
    key: 'school',
    label: 'Organization',
    type: 'text',
    required: true,
    wrap: 'ellipsis',
    sortable: true,
    searchable: true,
    ...size(140, 80, 480),
  },
  {
    key: 'instructors',
    label: 'Instructor(s)',
    type: 'text',
    required: true,
    wrap: 'ellipsis',
    sortable: true,
    searchable: true,
    value: row => row.instructors[0] || null,
    searchText: row => row.instructors.join(' '),
    render: row => <InstructorBadges row={row} />,
    ...size(220, 220, 480),
  },
  {
    key: 'ai',
    label: 'AI',
    type: 'text',
    exportable: false,
    render: row => <AiCell row={row} />,
    className: 'admin-courses-ai-cell',
    ...size(56, 56, 96),
  },
  {
    key: 'status',
    label: 'Status',
    type: 'badge',
    filterable: true,
    options: STATUS_OPTIONS,
    sortable: true,
    sortOrder: STATUS_OPTIONS,
    value: row => row.status.label,
    ...size(120, 80, 480),
  },
  {
    key: 'students',
    label: 'Students',
    type: 'numeric',
    sortable: true,
    value: row => row.students,
    render: row => <StudentsCell row={row} />,
    ...size(104, 80, 480),
  },
  {
    key: 'created',
    label: 'Created',
    type: 'date',
    filterable: true,
    sortable: true,
    // a date reads as one value; the content floor then holds the column at its width
    wrap: 'nowrap',
    ...size(112, 80, 480),
  },
  {
    // a badge column only so it can take an enumerable filter: a text column cannot be filterable
    key: 'type',
    label: 'Type',
    type: 'badge',
    filterable: true,
    options: TYPE_OPTIONS,
    // the server applies the refinement; the component requires a test
    refinement: { option: 'Master', label: 'Unique Master', test: () => true },
    sortable: true,
    value: row => row.type,
    render: row => <TypeCell row={row} />,
    ...size(96, 80, 480),
  },
  {
    // the master's course number on a child course; search finds it through Course Number, which is required
    key: 'master_coursenum',
    label: 'Master',
    type: 'text',
    wrap: 'ellipsis',
    sortable: true,
    value: masterOf,
    render: row => <MasterCell row={row} />,
    ...size(120, 90, 480),
  },
]

// Header tools, secondary before primary; a viewer with neither role gets none.
const pageActions = ({ canCreate, canReset, onNewCourse, onResetDemo }) => {
  const actions = []
  if (canReset)
    actions.push({
      key: 'reset-demo',
      label: 'Reset Demo Course',
      treatment: 'secondary',
      onSelect: onResetDemo,
    })
  if (canCreate)
    actions.push({
      key: 'new-course',
      label: 'Create New Course',
      treatment: 'primary',
      onSelect: onNewCourse,
    })
  return actions
}

/**
 * The table configuration for one viewer.
 * @param {{canCreate?: boolean, canReset?: boolean, deletedExist?: boolean, onShowDeleted?: Function,
 *   onNewCourse?: Function, onResetDemo?: Function}} options
 */
export const tableConfig = ({
  canCreate = false,
  canReset = false,
  deletedExist = false,
  onShowDeleted,
  onNewCourse,
  onResetDemo,
} = {}) => {
  const emptyState = {
    title: 'No courses yet',
    description: 'Courses appear here as they are created.',
  }
  if (deletedExist && typeof onShowDeleted === 'function')
    emptyState.action = {
      label: 'Show deleted courses',
      onSelect: onShowDeleted,
    }
  return {
    tableId: TABLE_ID,
    title: 'Courses',
    headingLevel: 2,
    caption: 'Courses',
    rowKey: 'id',
    columns: columns(),
    defaultSort: { key: 'created', direction: 'descending' },
    pageSizes: PAGE_SIZES,
    defaultPageSize: 100,
    source: { mode: 'page' },
    searchLabel: 'Search courses',
    searchPlaceholder:
      'Search by title, course number, organization, or instructor email',
    rowActionsLabel: 'Open',
    minMenuWidth: 236,
    minTableWidth: 920,
    emptyState,
    pageActions: pageActions({ canCreate, canReset, onNewCourse, onResetDemo }),
  }
}

// The quick-navigation destinations, in menu order.
export const DESTINATIONS = [
  {
    key: 'reader',
    label: 'Reader',
    icon: 'fa-book-open',
    path: c => `/reader/web/${c}/`,
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: 'fa-gear',
    path: c => `/instructor/setup/${c}/`,
  },
  {
    key: 'admin_settings',
    label: 'Admin Settings',
    icon: 'fa-sliders',
    path: c => `/instructor/admin_setup/${c}/`,
  },
  {
    key: 'assessments',
    label: 'Assessments',
    icon: 'fa-clipboard-list',
    path: c => `/instructor/assessment/${c}/`,
  },
  {
    key: 'materials',
    label: 'Materials',
    icon: 'fa-folder-open',
    path: c => `/instructor/materials/${c}/`,
  },
  {
    key: 'people',
    label: 'People',
    icon: 'fa-users',
    path: c => `/instructor/people/${c}/`,
  },
]

// the route's first segment, shown beside the label
const hintOf = href => `/${href.split('/')[1]}/…`

/**
 * One row's quick-navigation items, each a plain native link.
 * @param {object} row
 */
export const rowActions = row => {
  const coursenum = encodeURIComponent(row.coursenum)
  return DESTINATIONS.map(destination => {
    const href = destination.path(coursenum)
    return {
      key: destination.key,
      label: destination.label,
      icon: destination.icon,
      href,
      hint: hintOf(href),
    }
  })
}
